import * as React from 'react'
import { createRoot, Root } from 'react-dom/client'

import { TouchableOpacity, View } from 'react-native'

import styled from '@theme/styled-components'
import Provider from '@theme/Provider'

const ANIM_DELAY = 200

interface Props {
  Content: () => React.ReactElement
  position?: ModalPosition
  cancel?: boolean
  root: 'portal_root' | 'toast_root' | 'alert_root'
  closeCallback?: () => void
}

const Component = ({ Content, position = 'center', cancel = true, root }: Props) => {
  const [opened, setOpened] = React.useState(false)

  const close = () => {
    setOpened(false)
    setTimeout(() => Modal.close(root), ANIM_DELAY)
  }

  React.useEffect(() => {
    setOpened(true)

    window.onpopstate = () => Modal.close()
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  return (
    <MainContainer opened={opened} position={position}>
      <Back onPress={cancel ? close : undefined} activeOpacity={1} />

      <ContentContainer>
        <Content />
      </ContentContainer>
    </MainContainer>
  )
}

const MainContainer = styled(View)<{ opened: boolean; position: ModalPosition }>`
  transition: opacity 0.2s;
  opacity: ${(props) => (props.opened ? 1 : 0)};
  height: 100vh;
  width: 100vw;
  align-items: center;
  justify-content: ${(props) =>
    props.position === 'top' ? 'flex-start' : props.position === 'bottom' ? 'flex-end' : 'center'};
  pointer-events: auto;
`

const Back = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.modalBackground};
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
`

const ContentContainer = styled(View)`
  margin: 40px;
`

const modalRoot: Record<'portal_root' | 'toast_root' | 'alert_root', Root | null | undefined> = {
  alert_root: undefined,
  portal_root: undefined,
  toast_root: undefined,
}

const closeCallbacks: Record<'portal_root' | 'toast_root' | 'alert_root', (() => void) | null | undefined> = {
  alert_root: undefined,
  portal_root: undefined,
  toast_root: undefined,
}

const checkAndCallCallback = (root: 'portal_root' | 'toast_root' | 'alert_root') => {
  if (closeCallbacks[root]) {
    closeCallbacks[root]!()
    closeCallbacks[root] = undefined
  }
}

const Modal = {
  open: ({ root = 'portal_root', ...props }: Props) => {
    const element = document.getElementById(root)

    if (element) {
      const rootRenderer = createRoot(element)

      // If there's an existing modal mounted for this root, call its closeCallback before replacing
      checkAndCallCallback(root)

      modalRoot[root]?.unmount()

      modalRoot[root] = rootRenderer

      rootRenderer.render(
        <Provider>
          <Component root={root} {...props} />
        </Provider>
      )
    }

    // store new closeCallback (if provided)
    closeCallbacks[root] = props.closeCallback
  },
  close: (root: 'portal_root' | 'toast_root' | 'alert_root' = 'portal_root') => {
    const rootRenderer = modalRoot[root]

    if (rootRenderer) {
      checkAndCallCallback(root)
      rootRenderer.unmount()
      modalRoot[root] = null
    }
  },
}

export default Modal
